import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    MAX_VIDEO_BYTES,
    VIDEO_SIZE_PRESETS,
    compressVideo,
    compressedVideoName,
    formatFileSize,
    isSupportedVideoFile,
} from './compressVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const DEFAULT_QUALITY = 70;

export default function CompressVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [quality, setQuality] = useState(DEFAULT_QUALITY);
    const [sizePreset, setSizePreset] = useState('original');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('compressVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_VIDEO_BYTES) {
            const msg = t('compressVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setQuality(DEFAULT_QUALITY);
        setSizePreset('original');
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setQuality(DEFAULT_QUALITY);
        setSizePreset('original');
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('compressVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'COMPRESS_FAILED') {
            return t('compressVideo.errors.failed');
        }
        return t('compressVideo.errors.failed');
    };

    const handleCompress = async () => {
        if (!file || processing) return;

        recordToolUsage('video/compress', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const preset = VIDEO_SIZE_PRESETS.find((p) => p.value === sizePreset);
            const blob = await compressVideo(
                file,
                { quality, maxDim: preset?.maxDim ?? null },
                (value) => setProgress(value),
            );

            const output = {
                name: compressedVideoName(file.name),
                originalSize: file.size,
                compressedSize: blob.size,
                blob,
            };
            setResult(output);

            const reduction = file.size > 0
                ? ((1 - blob.size / file.size) * 100).toFixed(1)
                : '0.0';
            notifications.show({
                title: t('common.success'),
                message: t('compressVideo.reductionNotice', { percent: toBnDigits(reduction) }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`compressVideo.howTo.step${i}`)),
        [t],
    );

    const savedBytes = result ? Math.max(0, result.originalSize - result.compressedSize) : 0;
    const savedPercent = result && result.originalSize > 0
        ? ((savedBytes / result.originalSize) * 100).toFixed(1)
        : '0.0';

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.compressedSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({t('compressVideo.saved', { percent: toBnDigits(savedPercent) })})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('compressVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.compressedSize),
        reductionPercent: savedPercent,
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/compress"
            subtitleKey="compressVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('compressVideo.compress'),
                onClick: handleCompress,
                disabled: !file || processing || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('compressVideo.dropTitle')}
                            hint={t('compressVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('compressVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('compressVideo.settingsTitle')}>
                    <div className="space-y-4">
                        <label htmlFor="compress-video-quality" className="block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('compressVideo.qualityLabel', { value: toBnDigits(quality) })}
                            </span>
                            <input
                                id="compress-video-quality"
                                type="range"
                                min={10}
                                max={100}
                                step={1}
                                value={quality}
                                onChange={(e) => {
                                    setQuality(Number(e.target.value));
                                    invalidateResult();
                                }}
                                className="w-full accent-violet-500"
                            />
                            <span className="block text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('compressVideo.qualityHint')}
                            </span>
                        </label>

                        <div className="space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('compressVideo.resolutionLabel')}
                            </span>
                            <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('compressVideo.resolutionHint')}
                            </p>
                            <Select
                                value={sizePreset}
                                onValueChange={(value) => {
                                    setSizePreset(value);
                                    invalidateResult();
                                }}
                            >
                                <SelectTrigger className="h-10 border-[#ECE8E4] bg-white text-[13px] dark:border-zinc-700 dark:bg-zinc-900">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {VIDEO_SIZE_PRESETS.map((preset) => (
                                        <SelectItem key={preset.value} value={preset.value}>
                                            {t(`compressVideo.resolutions.${preset.value}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('compressVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('compressVideo.previewEmpty')}
                    idleHint={t('compressVideo.previewEmptyHint')}
                    processingLabel={t('compressVideo.compressing')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('compressVideo.done')}
                    downloadLabel={t('compressVideo.download')}
                    downloads={resultDownloads}
                    downloadVariant="compress"
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('compressVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('compressVideo.compress')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.compressedSize)
                                        : formatFileSize(file.size)}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
